import { Component, computed, signal } from '@angular/core';
import { IHeaderAngularComp } from 'ag-grid-angular';
import { GridApi, IHeaderParams, IRowNode } from 'ag-grid-community';

type PageSelectState = 'none' | 'some' | 'all';

const REFRESH_EVENTS = [
  'selectionChanged',
  'paginationChanged',
  'modelUpdated',
] as const;

/**
 * Header checkbox of the selection column on a paginated checkbox grid
 * (WAP-1750): toggles the loaded rows of the current page only, so the SSRM
 * "select all" state is never set (warnings 195 / 199, WAP-1666). Ticks on
 * other pages are kept by ag-grid's selection service. Register it through
 * `selectionColumnDef` (`PAGE_SELECT_COLUMN_DEF`).
 */
@Component({
  selector: 'app-ag-page-select-header',
  templateUrl: './ag-page-select-header.component.html',
  styleUrl: './ag-page-select-header.component.scss',
  standalone: false,
})
export class AgPageSelectHeaderComponent implements IHeaderAngularComp {
  private api?: GridApi;
  private eGridHeader?: HTMLElement;
  private readonly pageRows = signal<IRowNode[]>([]);
  private readonly selectedOnPage = signal(0);

  protected readonly state = computed<PageSelectState>(() => {
    const selected = this.selectedOnPage();
    if (selected === 0) return 'none';
    return selected === this.pageRows().length ? 'all' : 'some';
  });
  protected readonly disabled = computed(() => this.pageRows().length === 0);

  private readonly sync = (): void => {
    const rows = this.api ? currentPageRows(this.api) : [];
    this.pageRows.set(rows);
    this.selectedOnPage.set(rows.filter((node) => node.isSelected()).length);
  };

  private readonly onHeaderKeydown = (event: KeyboardEvent): void => {
    if (event.key === ' ') {
      event.preventDefault();
      this.toggle();
    }
  };

  agInit(params: IHeaderParams): void {
    this.api = params.api;
    this.eGridHeader = params.eGridHeader;
    REFRESH_EVENTS.forEach((type) => this.api?.addEventListener(type, this.sync));
    this.eGridHeader.addEventListener('keydown', this.onHeaderKeydown);
    this.sync();
  }

  refresh(): boolean {
    return true;
  }

  destroy(): void {
    REFRESH_EVENTS.forEach((type) => this.api?.removeEventListener(type, this.sync));
    this.eGridHeader?.removeEventListener('keydown', this.onHeaderKeydown);
  }

  /** None or some ticked -> tick the page; all ticked -> untick the page. */
  toggle(): void {
    const nodes = this.pageRows();
    if (!this.api || nodes.length === 0) return;
    this.api.setNodesSelected({ nodes, newValue: this.state() !== 'all' });
  }
}

/** Loaded, selectable rows of the current page (stubs of a block still in flight excluded). */
function currentPageRows(api: GridApi): IRowNode[] {
  const pageSize = api.paginationGetPageSize();
  const start = api.paginationGetCurrentPage() * pageSize;
  const end = Math.min(start + pageSize, api.getDisplayedRowCount());
  const rows: IRowNode[] = [];
  for (let index = start; index < end; index++) {
    const node = api.getDisplayedRowAtIndex(index);
    if (node?.data && !node.stub && node.selectable) rows.push(node);
  }
  return rows;
}

/** `selectionColumnDef` for a paginated checkbox grid: the page-scoped header checkbox. */
export const PAGE_SELECT_COLUMN_DEF = {
  headerComponent: AgPageSelectHeaderComponent,
};
